import React, { useEffect, useState } from "react";
import { Box, Grid, TextField, Button, Autocomplete } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import CustomAlert from "../../Common/Components/CustomAlert";

function Company({
  isAdmin,
  company,
  emplacements,
  shareHoldersByEmplacement = [],
  onCompanyUpdate,
}) {
  const { t } = useTranslation(["Company", "Common"]);
  const [discountPayments, setDiscountPayments] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  const { control, reset, handleSubmit, watch } = useForm({
    defaultValues: {
      noAccpac: "",
      nom: "",
      dateOuverture: "",
      dateFermeture: "",
      noMarchand: "",
      noMarchandDC: "",
      passwordDC: "",
      escomptePaiement: "",
      nomEmplacement: "",
      accessCodeMEV: "",
      nomExploitant: "",
      PST: "",
      GST_HST: "",
      businessName: "",
    },
  });

  const userState = useSelector((state) => state.user);

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    fetchEscomptePaiement();
  }, []);

  useEffect(() => {
    if (!company) return;

    reset({
      //
      noAccpac: company.noAccpac || "",
      nom: company.nom || "",
      dateOuverture: company.dateOuverture
        ? dayjs(company.dateOuverture)
        : null,
      dateFermeture: company.dateFermeture
        ? dayjs(company.dateFermeture)
        : null,
      businessName: company.businessName || "",
      noMarchand: company.noMarchand,
      noMarchandDC: company.noMarchandDC || 0,
      passwordDC: company.passwordDC || "",
      escomptePaiement:
        userState.lang === "EN"
          ? company.escomptePaiementDetailEn
          : company.escomptePaiementDetailFr || "",
      nomEmplacement: company.nomEmplacement || "",
      accessCodeMEV: company.accessCodeMEV || "",
      nomExploitant: company.nomExploitant || "",
      pst: company.pst || "",
      gsT_HST: company.gsT_HST || "",
    });
  }, [company, reset, userState.lang]);

  const fermetureValue = watch("dateFermeture");
  const isCompanyClosed = fermetureValue
    ? dayjs(fermetureValue).isBefore(dayjs(), "day")
    : false;

  const fetchEscomptePaiement = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/GetEscomptePaiement`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      setDiscountPayments(await response.json());
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmit = async (formData) => {
    const dataCompany = {
      noAccpac: formData.noAccpac,
      dateOuverture: formData.dateOuverture,
      dateFermeture: formData.dateFermeture,
      businessName: formData.businessName,
      nomExploitant: formData.nomExploitant,
      NoMarchand: formData.noMarchand === "" ? null : formData.noMarchand,
      noMarchandDC: formData.noMarchandDC === "" ? null : formData.noMarchandDC,
      nomEmplacement: formData.nomEmplacement,
      nom: formData.nom,
      PasswordDC: formData.passwordDC,
      escomptePaiement: formData.escomptePaiement,
      AccessCodeMEV: formData.accessCodeMEV,
      PST: formData.pst,
      GST_HST: formData.gsT_HST,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/SaveCompany`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(formData),
        },
      );
      if (!response.ok) throw new Error();
      setAlert({ show: true, type: "success", message: t("SaveSuccess") });
      onCompanyUpdate?.();
    } catch {
      setAlert({ show: true, type: "error", message: t("SaveError") });
    }
  };

  return (
    <>
      <Box sx={{ p: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Store Number */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="noAccpac"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("StoreNumber")}
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>

            {/* Company Name */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="nom"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("CompanyName")}
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="nomEmplacement"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={emplacements || []}
                    getOptionLabel={(o) => o?.nomEmplacement || ""}
                    value={
                      emplacements?.find(
                        (e) => e.nomEmplacement === field.value,
                      ) || null
                    }
                    onChange={(_, v) => field.onChange(v?.nomEmplacement || "")}
                    renderInput={(p) => (
                      <TextField {...p} label={t("Location")} size="small" />
                    )}
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>

            {/* Operator */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="nomExploitant"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={
                      value
                        ? (shareHoldersByEmplacement ?? []).find((s) => {
                            if (!s) return false;
                            const fullName =
                              `${s.firstname} ${s.lastName} (${s.username})`.trim();
                            return fullName === value;
                          }) || null
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        const fullName =
                          `${newValue.firstname} ${newValue.lastName} (${newValue.username})`.trim();
                        onChange(fullName);
                      } else onChange(null);
                    }}
                    options={shareHoldersByEmplacement ?? []}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.firstname} ${option.lastName} (${option.username})`.trim()
                        : ""
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Operator")}
                        fullWidth
                        size="small"
                      />
                    )}
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>

            {/* Opening Date */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateOuverture"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={t("OpenDate")}
                      value={value ? dayjs(value) : null}
                      onChange={(v) =>
                        onChange(v ? v.format("YYYY-MM-DD") : null)
                      }
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>

            {/* Close Date */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateFermeture"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={t("CloseDate")}
                      value={value ? dayjs(value) : null}
                      onChange={(v) =>
                        onChange(v ? v.format("YYYY-MM-DD") : null)
                      }
                      minDate={watch("dateOuverture")}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            {/* Business Name */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="businessName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("BusinessName")}
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>
            {/* Merchant Numbers */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="noMarchand"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === null || value === "") return true;
                    return /^\d+$/.test(value) || t("ValidateNoMarchand");
                  },
                  setValueAs: (value) => (value === "" ? null : value),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="No Marchand"
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Grid>

            {/* noMarchandDC Numbers */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="noMarchandDC"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === null || value === "") return true;
                    return /^\d+$/.test(value) || t("ValidateNoMarchandDc");
                  },
                  setValueAs: (value) => (value === "" ? null : value),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="No Marchand DC"
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Grid>
            {/* Access & Password */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="accessCodeMEV"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Code acces MEV"
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="passwordDC"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Mot de passe DC"
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>

            {/* GST/HST & PST */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="gsT_HST"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="GST/HST"
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                    InputLabelProps={{ shrink: true }}   // force le label en mode flottant
                    InputProps={{ notched: true }}     
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="pst"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="PST"
                    fullWidth
                    size="small"
                    disabled={isCompanyClosed}
                    InputLabelProps={{ shrink: true }}   // force le label en mode flottant
                    InputProps={{ notched: true }}    
                  />
                )}
              />
            </Grid>

            {/* Payment Discount */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="escomptePaiement"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={discountPayments ?? []}
                    getOptionLabel={(option) =>
                      userState.lang === "FR"
                        ? option.nomFr || ""
                        : option.nomEn || ""
                    }
                    value={
                      field.value
                        ? discountPayments.find(
                            (d) =>
                              d.nomFr === field.value ||
                              d.nomEn === field.value,
                          )
                        : null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(
                        newValue
                          ? userState.lang === "FR"
                            ? newValue.nomFr
                            : newValue.nomEn
                          : "",
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("PaymentDiscount")}
                        fullWidth
                        size="small"
                      />
                    )}
                    disabled={isCompanyClosed}
                  />
                )}
              />
            </Grid>
            {/* Payment Discount */}
            <Grid item xs={12} sm={6}>
              {/* Autocomplete Payment Discount */}
            </Grid>
            {/* Save Button juste en dessous */}
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
            >
              <Button
                variant="contained"
                type="submit"
                disabled={!isAdmin && !company?.estActif}
              >
                {t("Save")}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>

      <CustomAlert
        open={alert.show}
        closeMessage={() => setAlert((p) => ({ ...p, show: false }))}
        typeMessage={alert.type}
        message={alert.message}
      />
    </>
  );
}

export default Company;
